import { FileEditAltIcon, Paragraph, PlusIcon, ResponsiveButton, TextInput } from "@datagame/ui";
import { useState, ChangeEvent, KeyboardEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { ErrorMessage } from "../../../../../../shared/ui/ErrorMessage";
import { GAME_IMAGE_ATTRS } from "../../../../../../../constants";
import { GameKind } from "../../../../../../../gql/graphql";
import { useGame } from "../../../../../../../contexts/game/GameContext";

import styles from "./AddGameForm.module.scss";

const cx = classNames.bind(styles);

export enum QuickPickGameKind {
  QuickPick = "QUICK_PICK",
}

export type GameKindWithQuickPick = QuickPickGameKind | GameKind;

const GAMES_ORDER: GameKindWithQuickPick[] = [
  GameKind.Swiper,
  GameKind.QuickRank,
  GameKind.Rankifier,
  GameKind.Wordopolis,
  QuickPickGameKind.QuickPick,
  GameKind.Trivia,
  GameKind.Wordle,
];

export const AddGameForm = () => {
  const { addGame, errors } = useGame();
  const inputRef = useRef<HTMLInputElement>(null);
  const { t: translation } = useTranslation("project");
  const t = (key: string) => translation(`addGames.${key}`);

  const [gameKind, setGameKind] = useState<GameKindWithQuickPick | null>(null);
  const [gameName, setGameName] = useState<string>("");
  const isAddingGame = gameKind !== null;

  useEffect(() => {
    if (isAddingGame) {
      inputRef.current?.focus();
    } else {
      setGameName("");
    }
  }, [gameKind]);

  const handleGameKindChange = (kind: GameKindWithQuickPick) => {
    setGameKind(gameKind === kind ? null : kind);
  };

  const handleGameNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGameName(e.target.value);
  };

  const handleEnterPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSubmitGame();
    }
  };

  const handleSubmitGame = async () => {
    if (!isAddingGame) {
      return;
    }

    const isQuickPick = gameKind === QuickPickGameKind.QuickPick;

    const game = await addGame(
      {
        name: gameName,
        kind: isQuickPick ? GameKind.QuickRank : gameKind,
      },
      isQuickPick,
    );

    if (game) {
      setGameKind(null);
    }
  };

  return (
    <>
      <div className={styles.gameKindButtons}>
        {GAMES_ORDER.map((kind) => (
          <div
            role="button"
            tabIndex={0}
            onKeyDown={() => handleGameKindChange(kind)}
            key={kind}
            className={cx("gameKindButton", { selected: gameKind === kind })}
            onClick={() => handleGameKindChange(kind)}
          >
            <div className={cx("gameImageWrapper", { wordle: kind === GameKind.Wordle })}>
              {/* eslint-disable-next-line jsx-a11y/alt-text */}
              <img {...GAME_IMAGE_ATTRS[kind]} />
            </div>

            <Paragraph className={styles.gameLabel} size="xs" color="secondary">
              {GAME_IMAGE_ATTRS[kind].alt}
            </Paragraph>
          </div>
        ))}
      </div>

      {isAddingGame && (
        <>
          <div className={styles.addGame}>
            <TextInput
              className={styles.input}
              error={errors.name}
              icon={<FileEditAltIcon />}
              onChange={handleGameNameChange}
              onKeyDown={handleEnterPress}
              placeholder={t("add.placeholder")}
              ref={inputRef}
              size="small"
              value={gameName}
            />

            <ResponsiveButton
              size="medium"
              className={styles.addGameInput}
              kind="outline"
              iconSide="left"
              icon={<PlusIcon />}
              onClick={handleSubmitGame}
            >
              {t("add.confirm")}
            </ResponsiveButton>
          </div>
          {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
        </>
      )}
    </>
  );
};
